/**
 * Shared trend analysis primitives used by both the frontend renderer
 * and the nightly backend sweep (trendAlertSweep.ts).
 *
 * No Prisma / AWS / Node / Next imports — pure TypeScript only.
 */

import { isFormSchemaV2 } from "./forms";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface FieldStatistics {
  mean: number;
  median: number;
  stdDev: number;
  min: number;
  max: number;
  q1: number;
  q3: number;
  iqr: number;
  count: number;
  recentMean: number;
  overallMean: number;
  trend: "declining" | "stable" | "improving";
  slope: number;
}

export interface TrendAlert {
  id: string;
  residentId: string;
  residentName: string;
  severity: "info" | "warning" | "critical";
  type:
    | "outlier"
    | "declining_trend"
    | "sudden_drop"
    | "increased_variance"
    | "missing_data";
  fieldName: string;
  formName: string;
  message: string;
  details: string;
  currentValue?: number;
  baselineValue?: number;
  zScore?: number;
  changePercentage?: number;
  detectedAt: Date;
  dataPoints?: number;
}

// ── Statistical helpers ────────────────────────────────────────────────────────

export function calculateSlope(values: number[]): number {
  const n = values.length;
  if (n < 2) return 0;
  const sumX = (n * (n - 1)) / 2;
  const sumY = values.reduce((a, b) => a + b, 0);
  const sumXY = values.reduce((s, v, i) => s + i * v, 0);
  const sumX2 = values.reduce((s, _, i) => s + i * i, 0);
  const denom = n * sumX2 - sumX * sumX;
  return denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
}

export function calculateStatistics(values: number[]): FieldStatistics {
  if (values.length === 0) {
    return {
      mean: 0,
      median: 0,
      stdDev: 0,
      min: 0,
      max: 0,
      q1: 0,
      q3: 0,
      iqr: 0,
      count: 0,
      recentMean: 0,
      overallMean: 0,
      trend: "stable",
      slope: 0,
    };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const mean = sorted.reduce((a, b) => a + b, 0) / n;

  const mid = Math.floor(n / 2);
  const median =
    n % 2 === 0
      ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2
      : (sorted[mid] ?? 0);

  const variance = sorted.reduce((s, v) => s + (v - mean) ** 2, 0) / n;
  const stdDev = Math.sqrt(variance);

  const q1 = sorted[Math.floor(n * 0.25)] ?? 0;
  const q3 = sorted[Math.floor(n * 0.75)] ?? 0;
  const iqr = q3 - q1;

  const recentValues = values.slice(-Math.min(7, n));
  const recentMean =
    recentValues.reduce((a, b) => a + b, 0) / recentValues.length;

  const slope = calculateSlope(values.slice(-Math.min(10, n)));
  const trend: FieldStatistics["trend"] =
    slope < -0.5 ? "declining" : slope > 0.5 ? "improving" : "stable";

  return {
    mean,
    median,
    stdDev,
    min: sorted[0] ?? 0,
    max: sorted[n - 1] ?? 0,
    q1,
    q3,
    iqr,
    count: n,
    recentMean,
    overallMean: mean,
    trend,
    slope,
  };
}

export function calculateZScore(
  value: number,
  mean: number,
  stdDev: number,
): number {
  if (stdDev === 0) return 0;
  return (value - mean) / stdDev;
}

export function isOutlierIQR(
  value: number,
  q1: number,
  q3: number,
  iqr: number,
): boolean {
  return value < q1 - 1.5 * iqr || value > q3 + 1.5 * iqr;
}

// ── Schema label extraction ────────────────────────────────────────────────────

/**
 * Build a fieldId → human-readable-label map from a raw form_template.schema.
 *
 * Handles both storage formats:
 *  - FormSchemaV2  { version: "2", sections: [{ elements: [{ kind: "field", type: "number", ... }] }] }
 *  - Legacy flat   { fields: [{ id, label, type }] }
 *
 * Only "number" fields are included (those are the only ones statistically analysable).
 * Returns an empty map for any other schema shape — callers fall back to using the field ID.
 */
export function extractNumericFieldLabels(
  schema: unknown,
): Map<string, string> {
  const labels = new Map<string, string>();

  if (isFormSchemaV2(schema)) {
    for (const section of schema.sections) {
      for (const el of section.elements) {
        if (el.kind === "field" && el.type === "number") {
          labels.set(el.id, el.label);
        }
      }
    }
    return labels;
  }

  // Legacy flat format: { fields: [{ id, label, type }] }
  if (
    typeof schema === "object" &&
    schema !== null &&
    Array.isArray((schema as { fields?: unknown }).fields)
  ) {
    for (const f of (schema as { fields: Record<string, unknown>[] }).fields) {
      if (f["type"] === "number") {
        labels.set(String(f["id"]), String(f["label"] ?? f["id"]));
      }
    }
  }

  return labels;
}
